// Best‑effort cleanup for any stuck modal overlays or portals
// that might remain after XHR/reload/navigation glitches.

export function closeStuckModals() {
  hideBlankModals();
  hideLargeIframes();
  restoreBodyScrollIfStuck();
}

function restoreBodyScrollIfStuck() {
  try {
    // Chats layout intentionally disables body scrolling.
    if (document.querySelector('.lp-app-shell')) return;

    // Don't interfere with legitimate open dialogs (Radix/shadcn).
    const hasOpenDialog = !!document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [aria-modal="true"][data-state="open"]',
    );
    if (hasOpenDialog) return;

    const body = document.body;
    const html = document.documentElement;

    const overflowLocked = body.style.overflow === 'hidden' || html.style.overflow === 'hidden';
    const pointerLocked = body.style.pointerEvents === 'none';
    const attrLocked = body.hasAttribute('data-scroll-locked') || html.hasAttribute('data-scroll-locked');
    const overscrollLocked = (html.style as any).overscrollBehavior === 'none';

    if (!overflowLocked && !pointerLocked && !attrLocked && !overscrollLocked) return;

    body.style.overflow = '';
    body.style.pointerEvents = '';
    body.removeAttribute('data-scroll-locked');

    html.style.overflow = '';
    (html.style as any).overscrollBehavior = '';
    html.removeAttribute('data-scroll-locked');
  } catch {}
}

function hideBlankModals() {
  try {
    const viewportArea = window.innerWidth * window.innerHeight;
    const panels = Array.from(document.querySelectorAll('[data-lp-modal], [role="dialog"][aria-modal="true"]')) as HTMLElement[];
    for (const panel of panels) {
      const rect = panel.getBoundingClientRect();
      const area = Math.max(0, rect.width) * Math.max(0, rect.height);
      const txt = (panel.textContent || '').trim();
      const hasUi = panel.querySelector('button,input,select,textarea,a,[role="button"],img,svg,video,canvas,[data-lp-modal-keep]');
      const looksHuge = area >= viewportArea * 0.35;
      const isEmpty = txt.length === 0 && !hasUi;
      if (looksHuge && isEmpty) {
        const root = panel.closest('#modal') as HTMLElement | null;
        const target = root || panel;
        target.style.display = 'none';
        target.style.pointerEvents = 'none';
        target.setAttribute('data-lp-hidden', 'blank-modal');
        if (!(target as any).__lpLogged) {
          (target as any).__lpLogged = true;
          console.info('[overlayGuard] hid blank modal', { area, viewportArea });
        }
      }
    }
  } catch {}
}

function hideLargeIframes() {
  try {
    const ww = window.innerWidth;
    const wh = window.innerHeight;
    const minArea = ww * wh * 0.45;
    const looksLikeOverlay = (el: Element | null) => {
      if (!el) return false;
      const cs = getComputedStyle(el);
      const zi = parseInt(cs.zIndex || '0', 10);
      const pos = cs.position;
      const fixedish = pos === 'fixed' || pos === 'absolute';
      return fixedish || zi >= 10;
    };
    const findOverlayContainer = (el: HTMLElement | null): HTMLElement | null => {
      let node: HTMLElement | null = el;
      while (node) {
        if (looksLikeOverlay(node)) return node;
        node = node.parentElement;
      }
      return null;
    };
    const iframes = Array.from(document.querySelectorAll('iframe')) as HTMLIFrameElement[];
    for (const f of iframes) {
      const cs = getComputedStyle(f);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
      const rect = f.getBoundingClientRect();
      const overlayNode = findOverlayContainer(f);
      const targetRect = overlayNode ? overlayNode.getBoundingClientRect() : rect;
      const area = Math.max(0, targetRect.width) * Math.max(0, targetRect.height);
      if (area < minArea) continue;
      const looksOverlay = looksLikeOverlay(f) || looksLikeOverlay(overlayNode);
      if (!looksOverlay) continue;
      const src = f.getAttribute('src') || '';
      try {
        const target = overlayNode || f;
        target.setAttribute('data-lp-hidden', 'iframe');
        (target as HTMLElement).style.display = 'none';
        (target as HTMLElement).style.pointerEvents = 'none';
        (target as HTMLElement).style.opacity = '0';
        if (!(target as any).__lpLogged) {
          (target as any).__lpLogged = true;
          console.info('[overlayGuard] hid large iframe', { rect: targetRect, src });
        }
      } catch {}
    }
  } catch {}
}

export function startOverlayWatcher() {
  try {
    const sweep = () => { hideBlankModals(); hideLargeIframes(); restoreBodyScrollIfStuck(); };
    sweep();
    const mo = new MutationObserver(() => sweep());
    mo.observe(document.documentElement, { childList: true, subtree: true });
    // Store ref on window to avoid GC in hot reloads
    (window as any).__lpOverlayMO = mo;
    // Re-run periodically as a safety net
    setInterval(sweep, 1500);
  } catch {}
}

// Close on initial load just in case
try { closeStuckModals(); } catch {}
